"use client"

import { useState } from "react"
import { ArrowRightLeft, Check, ChevronsUpDown, Handshake, Loader2 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  useLendPhoneNumberMutation,
  useTakeOverPhoneNumberLoanMutation,
} from "@/hooks/mutations/phone-number-loans"
import { cn } from "@/lib/utils"
import type { OutgoingPhoneNumber } from "@/lib/voice/outgoing-calls"

type OutgoingCallNumberSelectProps = {
  numbers: OutgoingPhoneNumber[]
  value: string
  loading: boolean
  disabled: boolean
  invalid?: boolean
  describedBy?: string
  onValueChange: (phoneNumber: string) => void
}

export function OutgoingCallNumberSelect({
  numbers,
  value,
  loading,
  disabled,
  invalid = false,
  describedBy,
  onValueChange,
}: OutgoingCallNumberSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [lendingPhoneNumber, setLendingPhoneNumber] = useState<string | null>(null)
  const lendNumber = useLendPhoneNumberMutation()
  const takeOverNumber = useTakeOverPhoneNumberLoanMutation()
  const selected = numbers.find((number) => !number.lendingSourceTenantId && number.phoneNumber === value)
  const normalizedSearch = search.trim().toLowerCase()
  const filteredNumbers = normalizedSearch
    ? numbers.filter((number) => `${number.label ?? ""} ${number.phoneNumber}`.toLowerCase().includes(normalizedSearch))
    : numbers

  async function handleLend(number: OutgoingPhoneNumber) {
    if (!number.lendingSourceTenantId || !number.lendingDestinationTenantId) return

    setLendingPhoneNumber(number.phoneNumber)
    try {
      const loan = number.lendingActiveLoanId
        ? await takeOverNumber.mutateAsync({
            loanId: number.lendingActiveLoanId,
            phoneNumber: number.phoneNumber,
            sourceTenantId: number.lendingSourceTenantId,
            destinationTenantId: number.lendingDestinationTenantId,
          })
        : await lendNumber.mutateAsync({
            phoneNumber: number.phoneNumber,
            sourceTenantId: number.lendingSourceTenantId,
            destinationTenantId: number.lendingDestinationTenantId,
          })
      onValueChange(loan.phoneNumber)
      setOpen(false)
    } catch {
      // withQueryHandling displays the action error and keeps the list open for retry.
    } finally {
      setLendingPhoneNumber(null)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id="outgoing-from-number"
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          className={cn(
            "w-full justify-between font-normal",
            invalid && "border-destructive focus-visible:ring-destructive",
          )}
          disabled={disabled}
        >
          {selected ? <PhoneNumberLabel number={selected} /> : (
            <span className="text-muted-foreground">
              {loading ? "Loading phone numbers..." : "Select a phone number"}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] space-y-2 p-2" align="start">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search phone numbers..."
          aria-label="Search phone numbers"
        />
        <div className="max-h-72 overflow-y-auto">
          {filteredNumbers.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">No phone numbers found.</p>
          ) : (
            filteredNumbers.map((number) => {
              const isLendable = Boolean(number.lendingSourceTenantId)
              const isLending = lendingPhoneNumber === number.phoneNumber
              const isAlreadyLent = Boolean(number.lendingActiveLoanId)
              const destinationTenantName =
                number.lendingActiveLoanDestinationTenantName ?? "another tenant"

              if (!isLendable) {
                return (
                  <button
                    key={`use:${number.id}`}
                    type="button"
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                    onClick={() => {
                      onValueChange(number.phoneNumber)
                      setOpen(false)
                    }}
                  >
                    <Check className={cn("h-4 w-4 shrink-0", value === number.phoneNumber ? "opacity-100" : "opacity-0")} />
                    <PhoneNumberLabel number={number} />
                  </button>
                )
              }

              return (
                <div
                  key={`lend:${number.id}`}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-sm px-2 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <PhoneNumberLabel number={number} />
                    {isAlreadyLent ? (
                      <div className="mt-1 flex min-w-0 items-center gap-1.5">
                        <Badge
                          variant="secondary"
                          className="shrink-0 whitespace-nowrap px-1.5 py-0 text-[11px]"
                        >
                          Already lent
                        </Badge>
                        <span
                          className="truncate text-xs text-muted-foreground"
                          title={`Currently lent to ${destinationTenantName}`}
                        >
                          Currently lent to {destinationTenantName}.
                        </span>
                      </div>
                    ) : (
                      <div className="mt-1 text-xs text-muted-foreground">Available to lend</div>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 shrink-0 whitespace-nowrap px-2.5 text-xs"
                    disabled={lendNumber.isPending || takeOverNumber.isPending}
                    onClick={() => void handleLend(number)}
                  >
                    <LendingButtonIcon isLending={isLending} isAlreadyLent={isAlreadyLent} />
                    <span>{isAlreadyLent ? "Take away & Lend here" : "Lend"}</span>
                  </Button>
                </div>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function LendingButtonIcon({
  isLending,
  isAlreadyLent,
}: {
  isLending: boolean
  isAlreadyLent: boolean
}) {
  if (isLending) return <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
  if (isAlreadyLent) return <ArrowRightLeft className="mr-1.5 h-3.5 w-3.5" />
  return <Handshake className="mr-1.5 h-3.5 w-3.5" />
}

function PhoneNumberLabel({ number }: { number: OutgoingPhoneNumber }) {
  const isDemo = Boolean(number.phoneNumberLoanId)
  const displayLabel = number.label ? `${number.label} (${number.phoneNumber})` : number.phoneNumber

  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate" title={displayLabel}>
        {displayLabel}
      </span>
      {isDemo ? <Badge className="bg-violet-100 text-violet-900 hover:bg-violet-100">Demo</Badge> : null}
    </span>
  )
}
